import React from "react";
import { createPageUrl } from "@/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Code, Calendar, Hash, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

export default function ViewProcessingRuleModal({ isOpen, onClose, rule }) {
  const navigate = useNavigate();

  if (!rule) return null;

  const handleOpenRule = () => {
    onClose();
    navigate(createPageUrl(`processing-rules`));
  };

  const getRuleTypeColor = (ruleType) => {
    const colorMap = {
      'validation': 'bg-blue-100 text-blue-800',
      'transformation': 'bg-purple-100 text-purple-800',
      'normalization': 'bg-green-100 text-green-800',
      'enrichment': 'bg-orange-100 text-orange-800'
    };
    return colorMap[ruleType] || 'bg-gray-100 text-gray-800';
  };

  const getLanguageColor = (language) => {
    const colorMap = {
      'javascript': 'bg-yellow-100 text-yellow-800',
      'python': 'bg-blue-100 text-blue-800',
      'sql': 'bg-cyan-100 text-cyan-800',
      'tiva_endpoint': 'bg-indigo-100 text-indigo-800'
    };
    return colorMap[language] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'Active': 'bg-green-100 text-green-800 border-green-200',
      'Draft': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Deprecated': 'bg-red-100 text-red-800 border-red-200'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!max-w-[90vw] w-[90vw] h-[90vh] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{rule.rule_name}</span>
            <Badge className={`${getStatusColor(rule.status)} border text-xs`}>
              {rule.status}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            {rule.description || 'No description provided'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Metadata Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Type</Label>
              <div>
                <Badge className={`${getRuleTypeColor(rule.rule_type)} text-xs capitalize`}>
                  {rule.rule_type}
                </Badge>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Language</Label>
              <div>
                <Badge className={`${getLanguageColor(rule.rule_language)} text-xs`}>
                  {rule.rule_language === 'tiva_endpoint' ? 'Tiva' : rule.rule_language}
                </Badge>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Input Type</Label>
              <div className="text-sm font-medium">{rule.input_type || 'string'}</div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Output Type</Label>
              <div className="text-sm font-medium">{rule.output_type || 'struct'}</div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
            <div className="flex items-center gap-1">
              <Hash className="w-4 h-4" />
              <span>Version {rule.version || 1}</span>
            </div>
            {rule.updated_date && (
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Updated {format(new Date(rule.updated_date), 'MMM d, yyyy')}</span>
              </div>
            )}
          </div>

          {/* Code Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-slate-600" />
              <Label className="font-semibold">Rule Code</Label>
            </div>
            <div className="relative border border-slate-700 rounded-lg">
              <div className="max-h-[400px] overflow-auto">
                <pre className="bg-slate-900 text-slate-100 p-4 text-sm font-mono whitespace-pre min-w-max">
                  {rule.rule_code || '// No code defined'}
                </pre>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Expected output format: {`{ isValid: boolean, transformedValue: any, message: string }`}
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-between pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleOpenRule}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Go to Processing Rules
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
